import { FunctionComponent, MouseEvent, useEffect, useRef, useState } from "react";
import { ActionPlanAppointmentItem } from "../../models/ActionPlan";
import { exportAppointmentToOutlook } from "../../utils/AppointmentCalendarExport";
import styles from "./AppointmentRegistrationModal.module.css";
import layoutStyles from "./AppointmentRegistrationModalLayout.module.css";

export interface AppointmentRegistrationModalProps {
    open: boolean;
    appointment?: ActionPlanAppointmentItem;
    onClose: () => void;
    onConfirm: (appointment: ActionPlanAppointmentItem) => void | Promise<void>;
}

const AppointmentRegistrationModal: FunctionComponent<AppointmentRegistrationModalProps> = ({
    open,
    appointment,
    onClose,
    onConfirm
}) => {
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string>();
    const cancelButtonRef = useRef<HTMLButtonElement>(null);

    useEffect(() => {
        if (!open) return;

        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        cancelButtonRef.current?.focus();

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === "Escape" && !submitting) {
                onClose();
            }
        };

        window.addEventListener("keydown", handleKeyDown);

        return () => {
            document.body.style.overflow = previousOverflow;
            window.removeEventListener("keydown", handleKeyDown);
        };
    }, [open, onClose, submitting]);

    useEffect(() => {
        if (!open) {
            setSubmitting(false);
            setError(undefined);
        }
    }, [open]);

    if (!open || !appointment) {
        return null;
    }

    const ended = isPast(appointment);

    const handleBackdropClick = (event: MouseEvent<HTMLDivElement>) => {
        if (event.target === event.currentTarget && !submitting) {
            onClose();
        }
    };

    const handleConfirm = async () => {
        if (appointment.isRegistered || ended) return;

        setSubmitting(true);
        setError(undefined);

        try {
            await onConfirm(appointment);
        } catch (reason) {
            console.error(reason);
            setError(reason instanceof Error && reason.message
                ? reason.message
                : "L'inscription n'a pas pu être confirmée. Veuillez réessayer.");
        } finally {
            setSubmitting(false);
        }
    };

    const dateLabel = appointment.start ? formatDate(appointment.start) : "Date à confirmer";
    const timeLabel = appointment.start ? formatTimeRange(appointment.start, appointment.end) : undefined;
    const full = appointment.participantCapacity !== undefined &&
        (appointment.participantCount ?? 0) >= appointment.participantCapacity;

    return (
        <div
            className={styles.backdrop}
            role="presentation"
            onMouseDown={handleBackdropClick}
        >
            <section
                className={styles.modal}
                role="dialog"
                aria-modal="true"
                aria-labelledby="appointment-registration-title"
            >
                <button
                    type="button"
                    className={styles.closeButton}
                    aria-label="Fermer la fenêtre d'inscription"
                    onClick={onClose}
                    disabled={submitting}
                >
                    ×
                </button>

                <div className={styles.contentGrid}>
                    <div className={styles.mainContent}>
                        <div className={styles.badges}>
                            <span>{appointment.type ?? "Atelier / formation"}</span>
                            {appointment.valueCodes.map((value) => (
                                <span key={value} className={styles.valueBadge}>{formatValue(value)}</span>
                            ))}
                        </div>

                        <h2 id="appointment-registration-title">{appointment.title}</h2>
                        {appointment.presenter && (
                            <p className={styles.presenter}>par <strong>{appointment.presenter}</strong></p>
                        )}

                        <dl className={styles.summaryList}>
                            <div>
                                <dt>Date</dt>
                                <dd>{dateLabel}</dd>
                            </div>
                            {timeLabel && (
                                <div>
                                    <dt>Horaire</dt>
                                    <dd>{timeLabel}</dd>
                                </div>
                            )}
                            <div>
                                <dt>Lieu</dt>
                                <dd>{appointment.location ?? "Lieu à confirmer"}</dd>
                            </div>
                        </dl>

                        <section className={styles.detailSection}>
                            <h3>Objectifs</h3>
                            {appointment.objectives.length > 0 ? (
                                <ul>
                                    {appointment.objectives.map((objective) => (
                                        <li key={objective}>{objective}</li>
                                    ))}
                                </ul>
                            ) : (
                                <p className={styles.emptyDetail}>Objectifs non renseignés.</p>
                            )}
                        </section>

                        <section className={styles.detailSection}>
                            <h3>Déroulé</h3>
                            <p className={appointment.schedule ? undefined : styles.emptyDetail}>
                                {appointment.schedule ?? "Déroulé non renseigné."}
                            </p>
                        </section>
                    </div>

                    <aside className={`${styles.registrationPanel} ${layoutStyles.offsetRegistrationPanel}`}>
                        <div className={styles.dateCard}>
                            <strong>{appointment.start ? formatCompactDate(appointment.start) : "À confirmer"}</strong>
                            {timeLabel && <span>{timeLabel}</span>}
                        </div>

                        <button
                            type="button"
                            className={styles.outlookButton}
                            onClick={() => exportAppointmentToOutlook(appointment)}
                            disabled={!appointment.start}
                            title={appointment.start
                                ? "Télécharger l'événement au format Outlook (.ics)"
                                : "La date doit être renseignée pour exporter l'événement"}
                        >
                            <span aria-hidden="true">▣</span>
                            Ajouter à Outlook
                        </button>

                        <div className={styles.participantBlock}>
                            <span>Participants</span>
                            <strong>
                                {appointment.participantCount ?? 0}
                                {appointment.participantCapacity !== undefined
                                    ? ` / ${appointment.participantCapacity}`
                                    : ""}
                            </strong>
                        </div>

                        <p>
                            {appointment.isRegistered
                                ? "Votre participation à cet événement est confirmée."
                                : ended
                                    ? "Cet événement est terminé."
                                    : full
                                        ? "Cette formation est complète."
                                        : "Confirmez votre participation à cet événement."}
                        </p>

                        {error && <p className={styles.error} role="alert">{error}</p>}

                        {appointment.isRegistered ? (
                            <div className={styles.registeredState}>✓ Inscrit</div>
                        ) : ended ? (
                            <div className={styles.registeredState}>Événement terminé</div>
                        ) : (
                            <button
                                type="button"
                                className={styles.primaryButton}
                                onClick={handleConfirm}
                                disabled={submitting || full}
                            >
                                {submitting ? "Inscription en cours…" : full ? "Complet" : "Je m'inscris"}
                            </button>
                        )}
                        <button
                            ref={cancelButtonRef}
                            type="button"
                            className={styles.secondaryButton}
                            onClick={onClose}
                            disabled={submitting}
                        >
                            Fermer
                        </button>
                    </aside>
                </div>
            </section>
        </div>
    );
};

const isPast = (appointment: ActionPlanAppointmentItem): boolean => {
    const reference = appointment.end ?? appointment.start;
    if (!reference) return false;
    return new Date(reference).getTime() < Date.now();
};

const formatValue = (value: string): string => value
    .split("-")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");

const formatDate = (value: string): string => new Intl.DateTimeFormat("fr-FR", {
    weekday: "long",
    day: "2-digit",
    month: "long",
    year: "numeric"
}).format(new Date(value));

const formatCompactDate = (value: string): string => new Intl.DateTimeFormat("fr-FR", {
    day: "2-digit",
    month: "2-digit",
    year: "2-digit"
}).format(new Date(value));

const formatTimeRange = (start: string, end?: string): string => {
    const formatter = new Intl.DateTimeFormat("fr-FR", {
        hour: "2-digit",
        minute: "2-digit"
    });

    return end
        ? `${formatter.format(new Date(start))} – ${formatter.format(new Date(end))}`
        : formatter.format(new Date(start));
};

export default AppointmentRegistrationModal;
